import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { fetchCommunityPageData } from '@actions/others';
import Button from '@base/Button/Button';
import Image from '@base/Image';
import H1 from '@base/H1/H1';
import H5 from '@base/H5/H5';
import P1 from '@base/P1/P1';
import Paper from '@base/Paper/Paper';
import styled from '@emotion/styled';
import { mediaQuery, Spacings, Fonts } from '@styles/index';
import { useIsMobile } from '@stores/AppCommonData';
import useOneLinkSmartScript from '@hooks/useOneLinkSmartScript';

const Wrapper = styled(Paper)`
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  border-radius: 0;
  padding-left: 115px;
  padding-bottom: ${Spacings.SPACING_20B};
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(229, 244, 255, 0.44) 100%
  );

  ${mediaQuery.uptoTablet} {
    padding-left: 0;
    padding-right: 0;
  }
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    padding-bottom: ${Spacings.SPACING_8B};
    padding-left: 0;
    padding-right: 0;
  }
`;

const CommunityImageBox = styled.div`
  width: 380px;
  height: 498px;
  ${mediaQuery.uptoMobile} {
    margin: 0 auto;
    width: 208px;
    height: 300px;
  }
`;

const AboutCommunityContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const AboutCommunityHeader = styled.div`
  display: flex;
  flex-direction: column-reverse;
  ${mediaQuery.uptoMobile} {
    flex-direction: row;
    align-items: center;
  }
`;

const AboutCommunityImageBox = styled.div`
  width: 140px;
`;

const StyledH1 = styled(H1)`
  margin-top: ${Spacings.SPACING_8B};
  font-size: 40px;
  ${mediaQuery.uptoMobile} {
    margin-top: 0;
  }
`;

const AboutCommunityDescription = styled.div`
  width: 400px;
  display: flex;
  flex-direction: column;
  margin-top: ${Spacings.SPACING_6B};
  margin-bottom: ${({ $marginBottom }) => $marginBottom || 0};
  ${mediaQuery.uptoMobile} {
    align-items: ${({ $marginBottom }) =>
      !$marginBottom ? 'center' : 'unset'};
    width: 100%;
  }
`;

const AboutCommunityDescriptionPoint = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${({ $marginTop }) => ($marginTop ? Spacings.SPACING_4B : 0)};
  ${mediaQuery.uptoMobile} {
    align-items: baseline;
  }
`;

const StyledP1 = styled(P1)`
  font-size: ${Fonts.LARGE};
`;

const StyledPoint = styled(P1)`
  font-size: ${Fonts.LARGE};
  margin-left: ${Spacings.SPACING_2B};
`;

const StyledH5 = styled(H5)`
  color: var(--color-i-green);
`;

const CommunityTopSection = () => {
  const dispatch = useDispatch();
  const appDownloadLink = useOneLinkSmartScript();
  const isMobile = useIsMobile();
  const [data, setData] = useState({});

  const getData = async () => {
    const resp = await dispatch(fetchCommunityPageData());
    setData(JSON.parse(resp.result.description));
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetAppClick = () => {
    window.open(appDownloadLink, '_self');
  };

  return (
    <Wrapper gridColumn="1/24">
      <CommunityImageBox>
        <Image
          src="community/community-staticBg.png"
          width={380}
          height={498}
          alt="community"
        />
      </CommunityImageBox>
      <AboutCommunityContainer>
        <AboutCommunityHeader>
          <StyledH1>{data.title}</StyledH1>
          <AboutCommunityImageBox>
            <Image
              src="community/community-staticHeader.png"
              width={140}
              height={140}
              alt="logo"
            />
          </AboutCommunityImageBox>
        </AboutCommunityHeader>
        <AboutCommunityDescription>
          <StyledP1>{data.subtitle}</StyledP1>
          <AboutCommunityDescription $marginBottom={Spacings.SPACING_10B}>
            {data.description?.map((point, index) => (
              <AboutCommunityDescriptionPoint
                key={`${index + 1}`}
                $marginTop={index !== 0}
              >
                <Image
                  src="community/check.svg"
                  width={16}
                  height={16}
                  alt="check"
                />
                <StyledPoint>{point}</StyledPoint>
              </AboutCommunityDescriptionPoint>
            ))}
          </AboutCommunityDescription>
          {isMobile ? (
            <Button label="Get the app" onClick={handleGetAppClick} />
          ) : (
            <StyledH5>Get the Unacademy learner app on your mobile</StyledH5>
          )}
        </AboutCommunityDescription>
      </AboutCommunityContainer>
    </Wrapper>
  );
};

export default CommunityTopSection;
